import { createClient } from '@/lib/supabase/client'
import type { SubAccount, SubAccountWithStats } from '@/lib/types'

/**
 * Fetch all sub-accounts for a user
 */
export async function fetchUserSubAccounts(userId: string): Promise<SubAccount[]> {
  const supabase = createClient()

  const { data, error } = await ((supabase
    .from as any)('user_sub_accounts'))
    .select('*')
    .eq('user_id', userId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true })

  if (error) {
    console.error('Error fetching sub-accounts:', error)
    throw new Error('Failed to fetch sub-accounts')
  }

  return data.map((account: any) => ({
    id: account.id,
    userId: account.user_id,
    name: account.name,
    type: account.type as 'personal' | 'business' | 'savings' | 'other',
    icon: account.icon,
    color: account.color,
    isPrimary: account.is_primary,
    isActive: account.is_active,
    spendingLimit: account.spending_limit,
    createdAt: account.created_at,
    updatedAt: account.updated_at,
  }))
}

/**
 * Fetch primary sub-account for a user
 */
export async function fetchPrimarySubAccount(userId: string): Promise<SubAccount | null> {
  const supabase = createClient()

  const { data, error} = await ((supabase
    .from as any)('user_sub_accounts'))
    .select('*')
    .eq('user_id', userId)
    .eq('is_primary', true)
    .single()

  if (error) {
    console.error('Error fetching primary sub-account:', error)
    return null
  }

  // Explicit type annotation to bypass Supabase type inference issues
  const accountData: any = data

  return {
    id: accountData.id,
    userId: accountData.user_id,
    name: accountData.name,
    type: accountData.type as 'personal' | 'business' | 'savings' | 'other',
    icon: accountData.icon,
    color: accountData.color,
    isPrimary: accountData.is_primary,
    isActive: accountData.is_active,
    spendingLimit: accountData.spending_limit,
    createdAt: accountData.created_at,
    updatedAt: accountData.updated_at,
  }
}

/**
 * Fetch sub-accounts with statistics
 */
export async function fetchSubAccountsWithStats(
  userId: string
): Promise<SubAccountWithStats[]> {
  const supabase = createClient()

  const subAccounts = await fetchUserSubAccounts(userId)

  // Fetch statistics for each sub-account in parallel
  const accountsWithStats = await Promise.all(
    subAccounts.map(async (account) => {
      // Fetch card count
      const { count: cardCount } = await ((supabase
        .from('user_payment_cards') as any)
        .select('*', { count: 'exact', head: true })
        .eq('sub_account_id', account.id))

      // Fetch transaction count
      const [expensesCountResult, incomesCountResult] = await Promise.all([
        (supabase
          .from('user_expenses') as any)
          .select('*', { count: 'exact', head: true })
          .eq('sub_account_id', account.id),
        (supabase
          .from('user_incomes') as any)
          .select('*', { count: 'exact', head: true })
          .eq('sub_account_id', account.id),
      ])

      const transactionCount =
        (expensesCountResult.count || 0) + (incomesCountResult.count || 0)

      // Calculate balance (income - expenses)
      const [expensesResult, incomesResult] = await Promise.all([
        (supabase
          .from('user_expenses') as any)
          .select('amount')
          .eq('sub_account_id', account.id),
        (supabase
          .from('user_incomes') as any)
          .select('amount')
          .eq('sub_account_id', account.id),
      ])

      const expenses = expensesResult.data?.reduce(
        (sum, exp) => sum + Number(exp.amount),
        0
      ) || 0
      const income = incomesResult.data?.reduce(
        (sum, inc) => sum + Number(inc.amount),
        0
      ) || 0
      const balance = income - expenses

      // Calculate monthly spending (last 30 days)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const { data: recentExpenses } = await (supabase
        .from('user_expenses') as any)
        .select('amount')
        .eq('sub_account_id', account.id)
        .gte('created_at', thirtyDaysAgo.toISOString())

      const monthlySpending = recentExpenses?.reduce(
        (sum, exp) => sum + Number(exp.amount),
        0
      ) || 0

      // Calculate budget usage percentage
      let budgetUsage = 0
      if (account.spendingLimit && account.spendingLimit > 0) {
        budgetUsage = (monthlySpending / account.spendingLimit) * 100
      }

      return {
        ...account,
        balance,
        cardCount: cardCount || 0,
        transactionCount,
        monthlySpending,
        budgetUsage,
      }
    })
  )

  return accountsWithStats
}

/**
 * Create a new sub-account
 */
export async function createSubAccount(
  userId: string,
  account: Omit<SubAccount, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<SubAccount> {
  const supabase = createClient()

  const { data, error } = await ((supabase
    .from as any)('user_sub_accounts'))
    .insert({
      user_id: userId,
      name: account.name,
      type: account.type,
      icon: account.icon,
      color: account.color,
      is_primary: account.isPrimary,
      is_active: account.isActive,
      spending_limit: account.spendingLimit,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating sub-account:', error)
    throw new Error('Failed to create sub-account')
  }

  const accountData: any = data

  return {
    id: accountData.id,
    userId: accountData.user_id,
    name: accountData.name,
    type: accountData.type as 'personal' | 'business' | 'savings' | 'other',
    icon: accountData.icon,
    color: accountData.color,
    isPrimary: accountData.is_primary,
    isActive: accountData.is_active,
    spendingLimit: accountData.spending_limit,
    createdAt: accountData.created_at,
    updatedAt: accountData.updated_at,
  }
}

/**
 * Update an existing sub-account
 */
export async function updateSubAccount(
  accountId: string,
  updates: Partial<Omit<SubAccount, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>
): Promise<SubAccount> {
  const supabase = createClient()

  const updateData: any = {}
  if (updates.name !== undefined) updateData.name = updates.name
  if (updates.type !== undefined) updateData.type = updates.type
  if (updates.icon !== undefined) updateData.icon = updates.icon
  if (updates.color !== undefined) updateData.color = updates.color
  if (updates.isPrimary !== undefined) updateData.is_primary = updates.isPrimary
  if (updates.isActive !== undefined) updateData.is_active = updates.isActive
  if (updates.spendingLimit !== undefined)
    updateData.spending_limit = updates.spendingLimit

  const { data, error } = await ((supabase
    .from as any)('user_sub_accounts'))
    .update(updateData)
    .eq('id', accountId)
    .select()
    .single()

  if (error) {
    console.error('Error updating sub-account:', error)
    throw new Error('Failed to update sub-account')
  }

  const accountData: any = data

  return {
    id: accountData.id,
    userId: accountData.user_id,
    name: accountData.name,
    type: accountData.type as 'personal' | 'business' | 'savings' | 'other',
    icon: accountData.icon,
    color: accountData.color,
    isPrimary: accountData.is_primary,
    isActive: accountData.is_active,
    spendingLimit: accountData.spending_limit,
    createdAt: accountData.created_at,
    updatedAt: accountData.updated_at,
  }
}

/**
 * Delete a sub-account
 */
export async function deleteSubAccount(accountId: string): Promise<void> {
  const supabase = createClient()

  const { error } = await ((supabase
    .from as any)('user_sub_accounts'))
    .delete()
    .eq('id', accountId)

  if (error) {
    console.error('Error deleting sub-account:', error)
    throw new Error('Failed to delete sub-account')
  }
}

/**
 * Set a sub-account as the primary account
 */
export async function setDefaultSubAccount(
  userId: string,
  accountId: string
): Promise<void> {
  const supabase = createClient()

  // Database trigger handles unsetting other primary accounts
  const { error } = await ((supabase
    .from as any)('user_sub_accounts'))
    .update({ is_primary: true })
    .eq('id', accountId)
    .eq('user_id', userId)

  if (error) {
    console.error('Error setting default sub-account:', error)
    throw new Error('Failed to set default sub-account')
  }
}

/**
 * Toggle sub-account active status
 */
export async function toggleSubAccountActive(
  accountId: string,
  isActive: boolean
): Promise<void> {
  const supabase = createClient()

  const { error } = await ((supabase
    .from as any)('user_sub_accounts'))
    .update({ is_active: isActive })
    .eq('id', accountId)

  if (error) {
    console.error('Error toggling sub-account active status:', error)
    throw new Error('Failed to toggle sub-account active status')
  }
}

/**
 * Check if spending is within limit for a sub-account
 */
export async function checkSpendingLimit(
  accountId: string,
  newExpenseAmount: number
): Promise<{ withinLimit: boolean; currentSpending: number; limit: number | null }> {
  const supabase = createClient()

  // Fetch sub-account details
  const { data: account, error: accountError } = await ((supabase
    .from as any)('user_sub_accounts'))
    .select('spending_limit')
    .eq('id', accountId)
    .single()

  if (accountError || !account) {
    throw new Error('Failed to fetch sub-account details')
  }

  // If no spending limit set, always within limit
  if (!account.spending_limit) {
    return { withinLimit: true, currentSpending: 0, limit: null }
  }

  // Calculate current month's spending
  const startOfMonth = new Date()
  startOfMonth.setDate(1)
  startOfMonth.setHours(0, 0, 0, 0)

  const { data: expenses } = await ((supabase
    .from as any)('user_expenses'))
    .select('amount')
    .eq('sub_account_id', accountId)
    .gte('created_at', startOfMonth.toISOString())

  const currentSpending = expenses?.reduce(
    (sum, exp) => sum + Number(exp.amount),
    0
  ) || 0

  const totalWithNewExpense = currentSpending + newExpenseAmount
  const withinLimit = totalWithNewExpense <= account.spending_limit

  return {
    withinLimit,
    currentSpending,
    limit: account.spending_limit,
  }
}

/**
 * Get sub-account statistics
 */
export async function getSubAccountStats(accountId: string): Promise<{
  balance: number
  income: number
  expenses: number
  cardCount: number
  transactionCount: number
}> {
  const supabase = createClient()

  // Fetch data in parallel
  const [
    { data: expensesData },
    { data: incomesData },
    { count: cardCount },
    { count: expenseCount },
    { count: incomeCount },
  ] = await Promise.all([
    (supabase.from('user_expenses') as any).select('amount').eq('sub_account_id', accountId),
    (supabase.from('user_incomes') as any).select('amount').eq('sub_account_id', accountId),
    (supabase
      .from('user_payment_cards') as any)
      .select('*', { count: 'exact', head: true })
      .eq('sub_account_id', accountId),
    (supabase
      .from('user_expenses') as any)
      .select('*', { count: 'exact', head: true })
      .eq('sub_account_id', accountId),
    (supabase
      .from('user_incomes') as any)
      .select('*', { count: 'exact', head: true })
      .eq('sub_account_id', accountId),
  ])

  const expenses = expensesData?.reduce((sum, exp) => sum + Number(exp.amount), 0) || 0
  const income = incomesData?.reduce((sum, inc) => sum + Number(inc.amount), 0) || 0
  const balance = income - expenses

  return {
    balance,
    income,
    expenses,
    cardCount: cardCount || 0,
    transactionCount: (expenseCount || 0) + (incomeCount || 0),
  }
}
